import type { AgentEconomics } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Coins, Gauge, Landmark, ShieldHalf, TrendingUp, Wallet } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { StatTile } from "@/components/domain/stat";
import { ErrorPanel } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { Tooltip } from "@/components/ui/tooltip";
import { ROUTES } from "@/features/analytics-shared/links";
import { EconomicsWaterfall } from "@/features/outcomes/charts";
import { q } from "@/lib/api";
import { fmtCost, fmtInt, fmtMoney, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/outcomes/economics")({
  validateSearch: z.object({ agentId: z.string().min(1).optional().catch(undefined) }),
  component: EconomicsPage,
});

const money = (n: number) => fmtMoney(n, { compact: true });

function fmtRoi(roi: number) {
  return Math.abs(roi) >= 10 ? `${fmtInt(Math.round(roi))}×` : `${roi.toFixed(1)}×`;
}

function Signed({ n }: { n: number }) {
  return <span className={cn("font-medium tabular", n < 0 ? "text-critical-text" : "text-fg")}>{n < 0 ? `−${money(-n)}` : money(n)}</span>;
}

function EconomicsPage() {
  const { agentId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const econ = useQuery(q.economics());
  const rows = useMemo(() => (econ.data ? [...econ.data].sort((a, b) => b.value.amount - a.value.amount) : undefined), [econ.data]);
  const selected = rows?.find((r) => r.agentId === agentId) ?? rows?.[0];
  const select = (id: string) => void navigate({ search: { agentId: id }, replace: true, resetScroll: false });

  const totals = useMemo(() => {
    const t = { value: 0, cost: 0, loss: 0, premium: 0, net: 0 };
    for (const e of rows ?? []) {
      t.value += e.value.amount;
      t.cost += e.cost.total.amount;
      t.loss += e.expectedLoss.amount;
      t.premium += e.premium.amount;
      t.net += e.net.amount;
    }
    return t;
  }, [rows]);

  const columns = useMemo<ColumnDef<AgentEconomics, unknown>[]>(
    () => [
      { id: "agent", header: "Agent", accessorKey: "agentName", cell: ({ row }) => <span className="font-mono text-[12.5px] text-fg">{row.original.agentName}</span> },
      { id: "sessions", header: "Sessions 30d", accessorKey: "sessions30d", cell: ({ row }) => <span className="text-fg-2">{fmtInt(row.original.sessions30d)}</span>, meta: { align: "right" }, sortDescFirst: true },
      { id: "value", header: "Value", accessorFn: (e) => e.value.amount, cell: ({ row }) => <span className="font-medium text-fg">{money(row.original.value.amount)}</span>, meta: { align: "right" }, sortDescFirst: true },
      {
        id: "cost",
        header: "Cost",
        accessorFn: (e) => e.cost.total.amount,
        cell: ({ row }) => (
          <Tooltip content={`Model ${fmtMoney(row.original.cost.model)} · tools ${fmtMoney(row.original.cost.tools)}`}>
            <span tabIndex={0} className="text-fg-2">
              {money(row.original.cost.total.amount)}
            </span>
          </Tooltip>
        ),
        meta: { align: "right" },
      },
      { id: "loss", header: "Expected loss", accessorFn: (e) => e.expectedLoss.amount, cell: ({ row }) => <span className="text-fg-2">{money(row.original.expectedLoss.amount)}</span>, meta: { align: "right" }, sortDescFirst: true },
      { id: "premium", header: "Premium", accessorFn: (e) => e.premium.amount, cell: ({ row }) => <span className="text-fg-2">{money(row.original.premium.amount)}</span>, meta: { align: "right" } },
      { id: "net", header: "Net", accessorFn: (e) => e.net.amount, cell: ({ row }) => <Signed n={row.original.net.amount} />, meta: { align: "right" }, sortDescFirst: true },
      { id: "roi", header: "ROI", accessorKey: "roi", cell: ({ row }) => <span className="text-fg-2">{fmtRoi(row.original.roi)}</span>, meta: { align: "right" }, sortDescFirst: true },
      { id: "cps", header: "Cost / success", accessorKey: "costPerSuccess", cell: ({ row }) => <span className="text-fg-2">{fmtCost(row.original.costPerSuccess)}</span>, meta: { align: "right" } },
      {
        id: "model",
        header: "Value model",
        accessorKey: "valueModel",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="block max-w-[220px] truncate text-xs text-fg-3" title={row.original.valueModel}>
            {row.original.valueModel}
          </span>
        ),
        meta: { responsive: "hidden 2xl:table-cell" },
      },
    ],
    [],
  );

  if (econ.error) return <ErrorPanel error={econ.error} reset={() => void econ.refetch()} />;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {rows ? (
          <>
            <StatTile label="Value delivered · 30d" icon={<TrendingUp />} value={money(totals.value)} sub="from your outcome definitions' value models" />
            <StatTile label="Model + tool cost" icon={<Wallet />} value={money(totals.cost)} sub={totals.value ? `${fmtPct(totals.cost / totals.value, 2)} of value` : undefined} />
            <StatTile label="Expected loss + premium" icon={<ShieldHalf />} value={money(totals.loss + totals.premium)} sub={`${money(totals.loss)} expected loss · ${money(totals.premium)} premium`} />
            <StatTile label="Net, risk-adjusted" icon={<Coins />} value={<Signed n={totals.net} />} sub="value − cost − expected loss − premium" />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-[104px]" />)
        )}
      </div>

      <Card>
        <CardHeader title="Per agent · last 30 days" description="Select an agent to see its waterfall and use cases" />
        <DataTable<AgentEconomics>
          data={rows}
          loading={econ.isPending}
          columns={columns}
          dense
          stickyHeader={false}
          getRowId={(e) => e.agentId}
          onRowClick={(e) => select(e.agentId)}
          rowClassName={(e) => (e.agentId === selected?.agentId ? "bg-accent-soft/40" : undefined)}
          empty={{ icon: <Coins />, title: "No economics yet", description: "Add value to your outcome definitions to see value delivered per agent." }}
        />
      </Card>

      {selected ? <AgentBreakdown e={selected} /> : null}
    </div>
  );
}

function AgentBreakdown({ e }: { e: AgentEconomics }) {
  const totalLoss = e.byUseCase.reduce((s, u) => s + u.expectedLoss.amount, 0);
  const totalValue = e.byUseCase.reduce((s, u) => s + u.value.amount, 0);
  return (
    <>
      <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader
            title={
              <>
                Value to net · <span className="font-mono text-[12.5px]">{e.agentName}</span>
              </>
            }
            description={e.valueModel}
          />
          <CardBody>
            <EconomicsWaterfall e={e} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="How it's computed" description="net = value − (model + tool cost) − expected loss − premium" />
          <CardBody className="flex flex-col gap-3 text-xs text-fg-2">
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px]">
              <dt className="text-fg-3">Successes</dt>
              <dd className="text-right tabular">{fmtInt(e.successes30d)} of {fmtInt(e.sessions30d)}</dd>
              <dt className="text-fg-3">Cost per success</dt>
              <dd className="text-right tabular">{fmtCost(e.costPerSuccess)}</dd>
              <dt className="text-fg-3">ROI</dt>
              <dd className="text-right tabular">{fmtRoi(e.roi)}</dd>
            </dl>
            <div className="flex gap-2 border-t pt-3">
              <Gauge className="mt-px size-3.5 shrink-0 text-fg-3" aria-hidden />
              <p>
                <span className="font-medium text-fg">Expected loss comes from the risk model:</span> incident frequency at the agent's current reliability score (ARS) × typical severity. Raising the score lowers it.{" "}
                <Link to={ROUTES.agentScore(e.agentId)} className="inline-flex items-center gap-0.5 text-accent hover:underline">
                  Agent score <ArrowUpRight className="size-3" aria-hidden />
                </Link>
              </p>
            </div>
            <div className="flex gap-2">
              <Landmark className="mt-px size-3.5 shrink-0 text-fg-3" aria-hidden />
              <p>
                <span className="font-medium text-fg">Premium</span> is this agent's share of the policies covering it, over 30 days.{" "}
                <Link to="/coverage" className="inline-flex items-center gap-0.5 text-accent hover:underline">
                  Policies <ArrowUpRight className="size-3" aria-hidden />
                </Link>
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="By use case" description="Use cases that carry much more of the expected loss than of the value are candidates for a platform control rather than for growth" />
        {e.byUseCase.length ? (
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-fg-3">
                  <th scope="col" className="px-4 py-2 font-medium">
                    Use case
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Sessions
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Value
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Cost
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Expected loss
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Net
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Share of loss vs value
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...e.byUseCase]
                  .sort((a, b) => b.value.amount - a.value.amount)
                  .map((u) => {
                    const net = u.value.amount - u.cost.amount - u.expectedLoss.amount;
                    const lossShare = totalLoss ? u.expectedLoss.amount / totalLoss : 0;
                    const valueShare = totalValue ? u.value.amount / totalValue : 0;
                    const controlCandidate = lossShare >= 0.05 && lossShare >= 2 * valueShare;
                    return (
                      <tr key={u.useCaseId} className="border-b last:border-0">
                        <td className="px-4 py-2">
                          <span className="flex items-center gap-2">
                            <Link to={ROUTES.useCase(u.useCaseId)} className="text-fg hover:text-accent hover:underline">
                              {u.name}
                            </Link>
                            {controlCandidate ? (
                              <Tooltip content={`${fmtPct(lossShare)} of the agent's expected loss for ${fmtPct(valueShare)} of its value`}>
                                <Badge tone="warning" tabIndex={0}>
                                  <ShieldHalf /> Control candidate
                                </Badge>
                              </Tooltip>
                            ) : null}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular text-fg-2">{fmtInt(u.sessions)}</td>
                        <td className="px-3 py-2 text-right tabular text-fg">{money(u.value.amount)}</td>
                        <td className="px-3 py-2 text-right tabular text-fg-2">{money(u.cost.amount)}</td>
                        <td className="px-3 py-2 text-right tabular text-fg-2">{money(u.expectedLoss.amount)}</td>
                        <td className="px-3 py-2 text-right">
                          <Signed n={net} />
                        </td>
                        <td className="px-4 py-2">
                          <span className="grid w-40 grid-cols-[48px_minmax(0,1fr)] items-center gap-x-2 gap-y-0.5 text-[11px] text-fg-3">
                            <span>loss</span>
                            <span className="h-1.5 overflow-hidden rounded-sm bg-surface-3" role="meter" aria-valuenow={lossShare} aria-valuemin={0} aria-valuemax={1} aria-label={`Share of expected loss ${fmtPct(lossShare)}`}>
                              <span className="block h-full rounded-sm" style={{ width: `${lossShare * 100}%`, background: "var(--series-2)" }} />
                            </span>
                            <span>value</span>
                            <span className="h-1.5 overflow-hidden rounded-sm bg-surface-3" role="meter" aria-valuenow={valueShare} aria-valuemin={0} aria-valuemax={1} aria-label={`Share of value ${fmtPct(valueShare)}`}>
                              <span className="block h-full rounded-sm" style={{ width: `${valueShare * 100}%`, background: "var(--series-1)" }} />
                            </span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No use-case breakdown" description="Use cases appear once discovery has run for this agent." className="py-8" />
        )}
      </Card>
    </>
  );
}
